// Cave84 — useSavedViews hook (F8.2)
// TanStack Query wrappers for the savedViews service.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { SavedView, InventoryViewMode } from '@cave84/shared-types';
import {
  createSavedView,
  deleteSavedView,
  listSavedViews,
  updateSavedView,
  type SaveViewPayload,
} from '@/services/savedViews';
import type { InventoryFilters } from './useInventory';

// ─── query key ───────────────────────────────────────────────────────────────

const QK_SAVED_VIEWS = ['savedViews'] as const;

// ─── reads ───────────────────────────────────────────────────────────────────

export function useSavedViews(): UseQueryResult<SavedView[]> {
  return useQuery({
    queryKey: QK_SAVED_VIEWS,
    queryFn:  listSavedViews,
    staleTime: 60 * 1000, // 1 min — saved views rarely change mid-session
  });
}

// ─── mutations ───────────────────────────────────────────────────────────────

export function useCreateSavedView(): UseMutationResult<string, Error, SaveViewPayload> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createSavedView,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: QK_SAVED_VIEWS }); },
  });
}

export function useUpdateSavedView(): UseMutationResult<
  void,
  Error,
  { viewId: string } & SaveViewPayload
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ viewId, ...payload }) => updateSavedView(viewId, payload),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: QK_SAVED_VIEWS }); },
  });
}

export function useDeleteSavedView(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteSavedView,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: QK_SAVED_VIEWS }); },
  });
}

// ─── convenience re-export of payload type ───────────────────────────────────

export type { SaveViewPayload, InventoryViewMode };
export type { InventoryFilters };
